import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabase.js';
import { useAuth } from '../../lib/auth.js';
import { routes } from '../../routes.js';
import { Button, FormField, TextInput } from '../../components/ui/index.js';
import { ScopeWordmark } from '../../components/ScopeWordmark.js';

interface ResetPasswordProps {
  variant?: 'agency' | 'portal';
}

const resetSchema = z
  .object({
    password: z.string().min(8, 'At least 8 characters'),
    confirm: z.string(),
  })
  .refine((d) => d.password === d.confirm, {
    message: 'Passwords do not match',
    path: ['confirm'],
  });
type ResetValues = z.infer<typeof resetSchema>;

/**
 * Reset-password screen. Reached via the link emailed by ForgotPassword.
 * Supabase parses the recovery token in the URL hash on its own; we just
 * need to be in a recovery session and call updateUser({ password }).
 */
export function ResetPassword({ variant = 'agency' }: ResetPasswordProps) {
  const navigate = useNavigate();
  const { clearRecovery } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [hasRecoverySession, setHasRecoverySession] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetValues>({ resolver: zodResolver(resetSchema) });

  // Supabase handles the recovery token in the URL fragment on load.
  // The auth state changes to PASSWORD_RECOVERY when the token is consumed.
  useEffect(() => {
    const { data: subscription } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        setHasRecoverySession(true);
      }
    });
    // Also check synchronously in case the event already fired.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) setHasRecoverySession(true);
    });
    return () => subscription.subscription.unsubscribe();
  }, []);

  const loginPath = variant === 'portal' ? routes.portalLogin : routes.login;

  const onSubmit = async (values: ResetValues) => {
    setSubmitError(null);
    const { error } = await supabase.auth.updateUser({ password: values.password });
    if (error) {
      setSubmitError(error.message);
      return;
    }
    // Drop the recovery flag + sign out so they re-login with the new password
    // (clearer audit trail than silently elevating a recovery session to a
    // full session, and prevents stale tabs from holding privileged access).
    clearRecovery();
    await supabase.auth.signOut();
    setSubmitted(true);
    setTimeout(() => {
      navigate(loginPath, { replace: true });
    }, 1500);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas px-4 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <ScopeWordmark />
        </div>
        <div className="rounded-lg border border-scope-border bg-white p-8 shadow-scope">
          <h1 className="mb-1 text-2xl font-semibold tracking-tight text-scope-navy">
            Set a new password
          </h1>
          <p className="mb-6 text-sm text-scope-text-muted">
            Choose a password (at least 8 characters).
          </p>

          {!hasRecoverySession ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-status-amber bg-status-amber-bg px-3 py-3 text-sm text-status-amber">
                Open this page from the link in the password-reset email. The link is single-use and
                expires after a short time.
              </div>
              <Link
                to={loginPath}
                className="block text-center text-sm text-scope-text-muted hover:text-scope-navy"
              >
                Back to sign in
              </Link>
            </div>
          ) : submitted ? (
            <div className="rounded-lg border border-status-green bg-status-green-bg px-3 py-3 text-sm text-status-green">
              Password updated. Redirecting…
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <FormField label="New password" htmlFor="password" error={errors.password?.message}>
                <TextInput
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  {...register('password')}
                />
              </FormField>

              <FormField label="Confirm password" htmlFor="confirm" error={errors.confirm?.message}>
                <TextInput
                  id="confirm"
                  type="password"
                  autoComplete="new-password"
                  {...register('confirm')}
                />
              </FormField>

              {submitError && (
                <div className="rounded-lg border border-status-red bg-status-red-bg px-3 py-2 text-xs text-status-red">
                  {submitError}
                </div>
              )}

              <Button type="submit" className="w-full" loading={isSubmitting}>
                Update password
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
